import React from 'react';
import { UserIcon, ShieldCheckIcon, ComputerDesktopIcon } from '@heroicons/react/24/outline';

export type ViewMode = 'user' | 'admin' | 'sysadmin';

interface ModeSelectorProps {
  mode: ViewMode;
  onChange: (mode: ViewMode) => void;
  isSysadmin: boolean;
  collapsed?: boolean;
}

const ModeSelector: React.FC<ModeSelectorProps> = ({ mode, onChange, isSysadmin, collapsed = false }) => {
  if (!isSysadmin) {
    // For non-sysadmins, show simple admin toggle
    return (
      <div className={`flex flex-col items-center ${collapsed ? 'gap-2' : 'gap-1.5'}`}>
        <div className={`flex ${collapsed ? 'flex-col gap-2' : 'flex-row gap-2'}`}>
          <button
            onClick={() => onChange('user')}
            className={`
              relative flex items-center justify-center
              transition-all duration-200
              w-8 h-8
              bg-transparent
              rounded-lg
              focus:outline-none focus:ring-2 focus:ring-[#A3F93E] focus:ring-offset-2 focus:ring-offset-black
            `}
            title={collapsed ? 'Standard' : ''}
          >
            <UserIcon className={`w-4 h-4 ${mode === 'user' ? 'text-[#A3F93E]' : 'text-gray-400 hover:text-gray-300'}`} />
          </button>
          <button
            onClick={() => onChange('admin')}
            className={`
              relative flex items-center justify-center
              transition-all duration-200
              w-8 h-8
              bg-transparent
              rounded-lg
              focus:outline-none focus:ring-2 focus:ring-[#A3F93E] focus:ring-offset-2 focus:ring-offset-black
            `}
            title={collapsed ? 'Admin' : ''}
          >
            <ShieldCheckIcon className={`w-4 h-4 ${mode === 'admin' ? 'text-[#A3F93E]' : 'text-gray-400 hover:text-gray-300'}`} />
          </button>
        </div>
        {!collapsed && (
          <span className="text-xs text-gray-300 font-medium">
            {mode === 'admin' ? 'Admin' : 'Standard'}
          </span>
        )}
      </div>
    );
  }

  // For sysadmins, show 3-mode radio button selector
  const modes: { value: ViewMode; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
    { value: 'user', label: 'Standard', icon: UserIcon },
    { value: 'admin', label: 'Admin', icon: ShieldCheckIcon },
    { value: 'sysadmin', label: 'SysAdmin', icon: ComputerDesktopIcon },
  ];

  const currentModeLabel = modes.find(m => m.value === mode)?.label || 'User';

  return (
    <div className={`flex flex-col items-center ${collapsed ? 'gap-2' : 'gap-1.5'}`}>
      <div className={`flex ${collapsed ? 'flex-col gap-2' : 'flex-row gap-2'}`}>
        {modes.map((m) => {
          const isActive = mode === m.value;
          const Icon = m.icon;
          
          return (
            <button
              key={m.value}
              onClick={() => onChange(m.value)}
              className={`
                relative flex items-center justify-center
                transition-all duration-200
                w-8 h-8
                bg-transparent
                rounded-lg
                focus:outline-none focus:ring-2 focus:ring-[#A3F93E] focus:ring-offset-2 focus:ring-offset-black
              `}
              title={collapsed ? m.label : ''}
            >
              <Icon className={`w-4 h-4 ${isActive ? 'text-[#A3F93E]' : 'text-gray-400 hover:text-gray-300'}`} />
            </button>
          );
        })}
      </div>
      {!collapsed && (
        <span className="text-xs text-gray-300 font-medium">
          {currentModeLabel}
        </span>
      )}
    </div>
  );
};

export default ModeSelector;
